if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        // Autoplay (muted) any hosted video while its slide is in the viewport —
        // covers desktop scroll, desktop thumbnail switching, and mobile swipe.
        this.setupVideoAutoplay();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector("button");
            if (button) {
              button.addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            }
          });
        if (
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      setupVideoAutoplay() {
        // Hosted videos are deferred (the <video> lives in a <template> until activated).
        // Find slides whose template holds a <video autoplay>, wire their custom play/pause
        // toggle, and play them muted while in the viewport (pausing when scrolled/swiped
        // away). One IntersectionObserver covers desktop stacked/columns scroll, desktop
        // thumbnail switching (hidden slides never intersect), and the mobile swipe row.
        const deferredVideos = Array.from(
          this.elements.viewer.querySelectorAll("deferred-media")
        ).filter((deferred) => {
          const template = deferred.querySelector("template");
          return template && template.content.querySelector("video[autoplay]");
        });
        if (!deferredVideos.length) return;

        deferredVideos.forEach((deferred) => {
          this.setupVideoToggle(deferred);
          this.setupVideoProgress(deferred);
        });

        if (!("IntersectionObserver" in window)) {
          deferredVideos.forEach((deferred) => this.playDeferredVideo(deferred));
          return;
        }

        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const deferred = entry.target;
              if (entry.isIntersecting) {
                // Respect a manual pause: don't auto-resume a video the user paused.
                if (!deferred.dataset.userPaused) this.playDeferredVideo(deferred);
              } else {
                const video = deferred.querySelector("video");
                if (video) video.pause();
              }
            });
          },
          { threshold: 0.5 }
        );
        deferredVideos.forEach((deferred) => this.videoObserver.observe(deferred));
      }

      playDeferredVideo(deferred) {
        // loadContent() calls pauseAllMedia() every time, so only call it when the <video>
        // doesn't exist yet; once loaded, just resume playback.
        let video = deferred.querySelector("video");
        if (!video) {
          if (typeof deferred.loadContent === "function") deferred.loadContent(false);
          video = deferred.querySelector("video");
        }
        if (video) video.play().catch(() => {});
      }

      setupVideoToggle(deferred) {
        const toggle = deferred.querySelector(".product__video-toggle");
        if (!toggle) return;

        // Whole-area tap toggles play/pause. A manual pause is remembered (userPaused) so the
        // IntersectionObserver doesn't auto-resume it when the slide re-enters the viewport.
        toggle.addEventListener("click", () => {
          let video = deferred.querySelector("video");
          if (!video) {
            // Not loaded yet: loadContent() starts muted playback. (Don't call it once
            // loaded — it runs pauseAllMedia() and would defeat the pause toggle below.)
            delete deferred.dataset.userPaused;
            if (typeof deferred.loadContent === "function") deferred.loadContent(false);
            return;
          }
          if (video.paused) {
            delete deferred.dataset.userPaused;
            video.play().catch(() => {});
          } else {
            deferred.dataset.userPaused = "true";
            video.pause();
          }
        });

        // Keep the toggle in sync with real playback state. play/pause don't bubble, so listen
        // in the capture phase on the (always-present) deferred element; the <video> is injected
        // later by loadContent().
        deferred.addEventListener(
          "play",
          (event) => {
            if (event.target.matches && event.target.matches("video")) {
              toggle.classList.add("is-playing");
              toggle.setAttribute("aria-pressed", "true");
            }
          },
          true
        );
        deferred.addEventListener(
          "pause",
          (event) => {
            if (event.target.matches && event.target.matches("video")) {
              toggle.classList.remove("is-playing");
              toggle.setAttribute("aria-pressed", "false");
            }
          },
          true
        );
      }

      setupVideoProgress(deferred) {
        const bar = deferred.querySelector(".product__video-progress");
        if (!bar) return;
        const fill = bar.querySelector(".product__video-progress__fill");

        const setFill = (ratio) => {
          const clamped = Math.min(1, Math.max(0, ratio));
          if (fill) fill.style.transform = `scaleX(${clamped})`;
          bar.setAttribute("aria-valuenow", Math.round(clamped * 100));
        };

        // Advance the fill as the video plays. timeupdate doesn't bubble, so listen in the
        // capture phase on the deferred element (the <video> is injected later by loadContent).
        deferred.addEventListener(
          "timeupdate",
          (event) => {
            const video = event.target;
            if (!video.matches || !video.matches("video")) return;
            if (!video.duration || isNaN(video.duration)) return;
            setFill(video.currentTime / video.duration);
          },
          true
        );

        // Seek to the pointer position (tap or drag).
        const seekTo = (event) => {
          const video = deferred.querySelector("video");
          if (!video || !video.duration || isNaN(video.duration)) return;
          const rect = bar.getBoundingClientRect();
          const ratio = (event.clientX - rect.left) / rect.width;
          video.currentTime = Math.min(1, Math.max(0, ratio)) * video.duration;
          setFill(ratio);
        };

        let scrubbing = false;
        bar.addEventListener("pointerdown", (event) => {
          if (!deferred.querySelector("video") && typeof deferred.loadContent === "function") {
            deferred.loadContent(false);
          }
          scrubbing = true;
          bar.setPointerCapture(event.pointerId);
          seekTo(event);
        });
        bar.addEventListener("pointermove", (event) => {
          if (scrubbing) seekTo(event);
        });
        const endScrub = (event) => {
          scrubbing = false;
          if (bar.hasPointerCapture(event.pointerId))
            bar.releasePointerCapture(event.pointerId);
        };
        bar.addEventListener("pointerup", endScrub);
        bar.addEventListener("pointercancel", endScrub);

        // Keyboard: arrow keys seek by 5 seconds.
        bar.addEventListener("keydown", (event) => {
          const video = deferred.querySelector("video");
          if (!video || !video.duration) return;
          let delta = 0;
          if (event.key === "ArrowRight") delta = 5;
          else if (event.key === "ArrowLeft") delta = -5;
          else return;
          event.preventDefault();
          video.currentTime = Math.min(
            video.duration,
            Math.max(0, video.currentTime + delta)
          );
        });
      }

      onSlideChanged(event) {
        if (
          !event.detail ||
          !event.detail.currentElement ||
          !event.detail.currentElement.dataset
        ) {
          return;
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        const shouldScrollIntoView = options.scrollIntoView !== false;
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          if (!shouldScrollIntoView) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        if (
          activeThumbnail &&
          activeThumbnail.dataset &&
          activeThumbnail.dataset.mediaPosition
        ) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        const thumbnailButton = thumbnail.querySelector("button");
        if (thumbnailButton) {
          thumbnailButton.setAttribute("aria-current", true);
        }
        if (
          this.elements.thumbnails.isSlideVisible &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        )
          return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
